import React, { useEffect, useState } from 'react';
import { ICellRendererParams } from 'ag-grid-community';

import Button from '@/components/common/Button/Button';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { UserRole } from '@/config/roles.config';
import useDownloadUpliftFile from '../../hooks/useDownloadUpliftFile';
import { useProcessUpliftFile } from '../../hooks/useProcessUpliftFile';
import { useGetUploadedUpliftFiles } from '../../hooks/useGetUploadedUpliftFiles';
import { UPLIFT_UPLOADED_FILES_PER_PAGE } from '@/config/common.config';
import styles from './grid-components.module.scss';
import { useAddQueryToUrl } from '@/components/common/TagTabs/useAddQueryToUrl';
import { notificationService } from '@/services/notification/notification.client';

export default function DownloadAndProcessButtons({ data }: ICellRendererParams) {
  const { downloadFile } = useDownloadUpliftFile();
  const { processFile } = useProcessUpliftFile();
  const { getUploadedFiles } = useGetUploadedUpliftFiles();
  const { getValue } = useAddQueryToUrl();
  const { getUserRole } = useGetUserRole();
  const currentPage = parseInt(getValue('page') ?? '1', 10);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [userRole, setUserRole] = useState<UserRole>();

  useEffect(() => {
    getUserRole().then(setUserRole);
  }, [getUserRole]);

  const isFullAccess = userRole === UserRole.FULL_ACCESS;

  const updateList = () => {
    getUploadedFiles(currentPage, UPLIFT_UPLOADED_FILES_PER_PAGE);
  };

  const onProcessFile = () => {
    processFile(data.id, updateList, {
      onAllDuplicates: (count) => {
        if (isFullAccess) {
          setDuplicateCount(count);
          setShowDuplicateModal(true);
        } else {
          notificationService.open({
            title: 'Duplicate uplifts detected',
            text: `All ${count} uplift(s) in this file already exist`
              + ' as Pending or Applied. Processing was not allowed.',
            type: 'danger'
          });
        }
      }
    });
  };

  const onConfirmAllowDuplicates = () => {
    setShowDuplicateModal(false);
    processFile(data.id, updateList, { allowDuplicates: true });
  };

  const onDownloadFile = () => {
    downloadFile(data.id, data.fileName);
  };

  return (
    <div className={styles.btnContainer}>
      {data?.isDownloadable && (
        <Button
          onClick={onDownloadFile}
          buttonSize='small'
          className="mr-15"
        >
          Download
        </Button>
      )}

      {data?.isProcessable && (
        <Button
          onClick={onProcessFile}
          buttonSize='small'
        >
          Process
        </Button>
      )}

      {showDuplicateModal && isFullAccess && (
        <AgConfirmationModal
          title="Duplicate uplifts detected"
          isOpen={showDuplicateModal}
          onClose={() => setShowDuplicateModal(false)}
          onConfirm={onConfirmAllowDuplicates}
          closeText="Cancel"
          confirmText="Process anyway"
          closeOnConfirm={false}
        >
          <p>
            All {duplicateCount} uplift(s) in this file already exist as Pending or Applied.
            Would you like to process them anyway?
          </p>
        </AgConfirmationModal>
      )}
    </div>
  );
}
